/* @flow */
/* eslint-env es6,browser */
import { extend, intersect, difference, unique } from './coreutils';
import getCVars from './gputils';
import { logInfo } from './logger';
import { getAllCookies, getAllCookiesLength, deleteCookie } from './cookie';

// reporting const
const FIELD_SEP = '~';
const RECORD_SEP = '!';

/** List of known & critical cookies stored by EOW & SP, shouldn't be reported/deleted */
const OUR_COOKIES = [
  'cjar_3',
  'cjar_1',
  'cjar_eow',
  'stoken',
  'switch',
  'switch2',
  'LOGIN',
  'LOGIN_EOW',
  'CT',
  'brand',
  'WLSP',
  'usprivacy',
  'keepSignin',
  'MAGIC',
  'ajaxSupported'
];

const _defaultConfig = {
  checkInterval: 0,
  reportingLength: 4000,
  cleanupLength: 5000,
  forceCleanupLength: 0,
  dndCookies: [],
  cleanupCookies: [],
  minCleanupCookieLength: 100
};
class _cookieManager {
  _config: cookieManagerConfig;

  _win: WindowProxy;

  _doc: Document;

  _lastTS: number;

  constructor() {
    this._config = _defaultConfig;
    this._lastTS = 0;
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  /* eslint-disable-next-line no-unused-vars */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
  }

  /**
   * updates config
   *
   * @external cookieManagerConfig
   * @param {cookieManagerConfig} o - Config for cookie manager
   * @returns {undefined}
   */
  updateConfig(config: cookieManagerConfig = {}) {
    this._config = extend(true, {}, _defaultConfig, config);
    // add our hardcoded dnd Cookies entries
    this._config.dndCookies = unique([
      ...OUR_COOKIES,
      ...this._config.dndCookies
    ]);
    // run this once at the start up.
    this.checkAndCleanup();
  }

  _shouldCheck(): boolean {
    if (this._config.checkInterval <= 0) {
      // functionality is turned off
      return false;
    }
    if (this._config.checkInterval === 1) {
      // we should check for every call (a.k.a every page load) OR ad rotation
      return true;
    }
    const nowTS = Date.now();

    return nowTS - this._lastTS > this._config.checkInterval * 1000;
  }

  _cleanUp(
    // $FlowFixMe unclear-type
    cookieData: Object,
    cookieNames: Array<string>,
    allCookiesLength: number = 0
  ): Array<string> {
    if (allCookiesLength < this._config.cleanupLength) {
      // no need to cleanup here
      return [];
    }
    let matchedEntries = intersect(cookieNames, this._config.cleanupCookies);
    let bCheckEachCookieLength = this._config.minCleanupCookieLength > 0;
    if (
      this._config.forceCleanupLength > 0 &&
      allCookiesLength >= this._config.forceCleanupLength
    ) {
      // cookie is too big. Let's delete all cleanup cookies irrespective of length
      bCheckEachCookieLength = false;
    }
    // should we add min length check for cookie?
    if (bCheckEachCookieLength) {
      matchedEntries = matchedEntries.filter(name => {
        const val = cookieData[name] || '';
        return val.length >= this._config.minCleanupCookieLength;
      });
    }
    matchedEntries.forEach(name => {
      deleteCookie(name, this._doc);
    });
    return matchedEntries;
  }

  _report(
    // $FlowFixMe unclear-type
    cookieData: Object,
    cookieNames: Array<string>,
    allCookiesLength: number,
    cleanedupEntries: Array<string>
  ) {
    // skip opt-out check and get MD5 hash for reporting
    const { _uh4: md5Email = '-' } = getCVars(this._win, true);
    // omit cleanedup entries as they will be always logged
    const unprocessedNames = difference(cookieNames, cleanedupEntries);
    const sortedNames = unprocessedNames.sort((a, b) => {
      const aVal = `${cookieData[a]}`;
      const bVal = `${cookieData[b]}`;
      return bVal.length - aVal.length;
    });
    const postCleanupLength = getAllCookiesLength();
    const namesToReport = sortedNames.slice(0, 5); // max 5 entries.
    const reportEntries = [
      `${md5Email}${FIELD_SEP}${allCookiesLength}${FIELD_SEP}${postCleanupLength}`
    ];
    reportEntries.push(
      ...cleanedupEntries.map(name => {
        const val = cookieData[name] || '';
        return `${name}${FIELD_SEP}${val.length}${FIELD_SEP}C`;
      }),
      ...namesToReport.map(name => {
        const val = cookieData[name] || '';
        return `${name}${FIELD_SEP}${val.length}${FIELD_SEP}I`;
      })
    );
    logInfo(`cookieReport=${reportEntries.join(RECORD_SEP)}`);
  }

  checkAndCleanup() {
    if (!this._shouldCheck()) {
      return;
    }
    const allCookiesLength = getAllCookiesLength(this._doc);
    if (allCookiesLength < this._config.reportingLength) {
      // skip reporting and cleanup
      return;
    }

    // get all the cookies
    const cookieData = getAllCookies(this._doc);
    // Omit list of DND cookies. They are set by us. No need to
    // clear OR report it.
    const cookieNames = difference(
      Object.keys(cookieData),
      this._config.dndCookies
    );
    const cleanedupEntries = this._cleanUp(
      cookieData,
      cookieNames,
      allCookiesLength
    );

    // report now
    this._report(cookieData, cookieNames, allCookiesLength, cleanedupEntries);
    // store the timestamp
    this._lastTS = Date.now();
  }
}

const _cm: _cookieManager = new _cookieManager();
export default _cm;
